import QRCode from "qrcode";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { displayUrl, emailHref, messageHref, type ShareTarget } from "../core/primitives/share-links";

/**
 * Shares a page of the public site. A phone opens its own share sheet; anywhere
 * else, or when the phone has none, a sheet of our own: copy, email, message and
 * a QR code to show or print.
 */
export function ShareButton({
  target,
  heading,
  note,
  label = "Share",
  iconOnly = false,
  className = "btn-secondary btn-sm",
}: {
  target: ShareTarget;
  heading: string;
  note?: ReactNode;
  label?: string;
  iconOnly?: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(false);

  const share = () => void shareOrOpen(target, () => setOpen(true));

  return (
    <>
      <button
        type="button"
        onClick={share}
        aria-label={iconOnly ? heading : undefined}
        aria-haspopup="dialog"
        className={className}
      >
        <ShareIcon />
        {!iconOnly && label}
      </button>
      {open && <ShareSheet target={target} heading={heading} note={note} onClose={() => setOpen(false)} />}
    </>
  );
}

/** The phone's own share sheet where there is one; `openSheet` otherwise, or when it fails. */
export async function shareOrOpen(target: ShareTarget, openSheet: () => void): Promise<void> {
  const touch = window.matchMedia("(pointer: coarse)").matches;
  if (touch && typeof navigator.share === "function") {
    try {
      await navigator.share({ title: target.title, text: target.text, url: target.url });
      return;
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
    }
  }
  openSheet();
}

export function ShareSheet({
  target,
  heading,
  note,
  onClose,
}: {
  target: ShareTarget;
  heading: string;
  note?: ReactNode;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [qr, setQr] = useState<string | null>(null);
  const field = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(target.url);
      setCopied(true);
    } catch {
      field.current?.select();
    }
  }

  async function toggleQr() {
    if (qr) return setQr(null);
    setQr(await QRCode.toDataURL(target.url, { width: 320, margin: 2, errorCorrectionLevel: "M" }));
  }

  const option =
    "flex flex-col items-center gap-1.5 rounded-xl border border-white/10 bg-white/5 px-1 py-3 text-[13px]";

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4">
      <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-black/70" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={heading}
        className="modal relative flex w-full flex-col gap-4 p-5 pb-8 sm:max-w-sm sm:pb-5"
      >
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-[17px] font-semibold">{heading}</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="btn-ghost btn-sm">
            ✕
          </button>
        </div>

        <div className="flex gap-2">
          <input
            ref={field}
            readOnly
            value={displayUrl(target.url)}
            aria-label="Link"
            onFocus={(event) => event.currentTarget.select()}
            className="field min-w-0 flex-1 font-mono text-[13px]"
          />
          <button type="button" onClick={() => void copy()} className="btn-primary btn-sm min-w-[84px]">
            {copied ? "Copied" : "Copy"}
          </button>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <a href={emailHref(target)} className={option}>
            Email
          </a>
          <a href={messageHref(target)} className={option}>
            Message
          </a>
          <button type="button" onClick={() => void toggleQr()} aria-pressed={qr !== null} className={option}>
            QR code
          </button>
        </div>

        {qr && (
          <div className="flex flex-col items-center gap-2 rounded-xl bg-white p-4 text-black">
            <img src={qr} alt={`QR code for ${displayUrl(target.url)}`} width={200} height={200} />
            <span className="text-xs font-semibold">Scan to open</span>
          </div>
        )}

        {note && <p className="text-xs opacity-60">{note}</p>}
      </div>
    </div>
  );
}

function ShareIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3v12M7 8l5-5 5 5M5 13v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6" />
    </svg>
  );
}
